import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const empty = { slug: "", name: "", description: "", image_url: "", order: 100 };

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/categories");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/categories/${editing}`, form);
        toast.success("Updated");
      } else {
        await api.post("/categories", form);
        toast.success("Created");
      }
      setEditing(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({ slug: c.slug, name: c.name, description: c.description, image_url: c.image_url, order: c.order });
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    await api.delete(`/categories/${c.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-categories-page">
      <h1 className="font-heading text-3xl font-medium text-slate-950 mb-2">Categories</h1>
      <p className="text-slate-500 mb-6">Manage the 10 visual category toggles on the homepage.</p>

      <div className="grid lg:grid-cols-[1fr,400px] gap-6">
        <div className="space-y-3">
          {items.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-4" data-testid={`category-row-${c.slug}`}>
              <img src={c.image_url} alt={c.name} className="w-16 h-16 object-cover rounded-md" />
              <div className="flex-1">
                <div className="font-medium text-slate-950">{c.name}</div>
                <div className="text-xs text-slate-500 line-clamp-1">{c.description}</div>
                <div className="text-xs text-slate-400 mt-1">{c.project_count} projects · order {c.order}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => startEdit(c)} data-testid={`edit-cat-${c.slug}`} className="p-2 hover:bg-slate-100 rounded">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(c)} data-testid={`delete-cat-${c.slug}`} className="p-2 hover:bg-rose-50 rounded text-rose-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={save} className="bg-white border border-slate-200 rounded-lg p-6 space-y-3 lg:sticky lg:top-6 self-start" data-testid="category-form">
          <h2 className="font-heading text-lg font-medium text-slate-950 flex items-center gap-2">
            {editing ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {editing ? "Edit category" : "New category"}
          </h2>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" data-testid="cat-name" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
          <input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="slug" data-testid="cat-slug" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm font-mono" />
          <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
          <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Image URL" data-testid="cat-image-url" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
          <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0") })} placeholder="Display order" className="w-full px-4 py-2.5 rounded-md border border-slate-200 text-sm" />
          <div className="flex gap-2">
            <button type="submit" data-testid="cat-submit" className="flex-1 bg-slate-950 hover:bg-slate-800 text-white px-4 py-2.5 rounded-md text-sm">
              {editing ? "Update" : "Create"}
            </button>
            {editing && (
              <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="px-4 py-2.5 rounded-md text-sm border border-slate-200">
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
